import clsx from "clsx";
import { useState } from "react";
import { Modal } from "@/components/Modal/Modal";
import { ScanResults } from "@/components/Sidebar/AtsScan/ScanResults";
import { AppText } from "@/components/Text/AppText";
import { useAppSelector } from "@/hooks/types";
import { getIsModalOpen, getModalProps } from "@/store/modal/selectors";
import { IAtsScanResult } from "@/types/atsScan";
import { ModalTypes } from "@/types/modal";
import { getScoreTailwindClass } from "@/utils/scoreColors";

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
    });
};

export const AtsScanHistoryModal = () => {
    const isOpen = useAppSelector(getIsModalOpen(ModalTypes.ATS_SCAN_HISTORY));
    const modalProps = useAppSelector(
        getModalProps(ModalTypes.ATS_SCAN_HISTORY),
    ) as { scans: IAtsScanResult[] };

    const [selectedScanIndex, setSelectedScanIndex] = useState(0);
    const scans = modalProps?.scans || [];

    const selectedScan = scans[selectedScanIndex];

    return (
        <Modal
            modalType={ModalTypes.ATS_SCAN_HISTORY}
            open={isOpen}
            title="ATS Scan History"
            width="4xl"
        >
            <div className="flex h-[600px] gap-6">
                <div className="w-72 flex-shrink-0 border-r border-neutral-200">
                    <div className="mr-4 max-h-[520px] space-y-2 overflow-y-auto">
                        {scans.map((scan, index) => {
                            const isSelected = index === selectedScanIndex;

                            return (
                                <div
                                    key={index}
                                    className={clsx(
                                        "cursor-pointer rounded-lg border p-3 transition-all hover:border-neutral-300",
                                        isSelected
                                            ? "border-neutral-200 bg-neutral-100"
                                            : "border-neutral-200 bg-white hover:bg-neutral-50",
                                    )}
                                    onClick={() => setSelectedScanIndex(index)}
                                >
                                    <AppText
                                        variant="subheadings"
                                        className={getScoreTailwindClass(
                                            scan.score,
                                        )}
                                    >
                                        {scan.score}%
                                    </AppText>
                                    <AppText
                                        variant="regular"
                                        className="text-neutral-700"
                                    >
                                        {formatDate(scan.createdAt)}{" "}
                                        {formatTime(scan.createdAt)}
                                    </AppText>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {selectedScan ? (
                        <div className="space-y-6">
                            <div
                                className={`flex flex-col items-center justify-center ${getScoreTailwindClass(
                                    selectedScan.score,
                                )}`}
                            >
                                <AppText variant="headings">
                                    {selectedScan.score}%
                                </AppText>
                                <AppText variant="labelsbuttons">
                                    Resume Score
                                </AppText>
                            </div>

                            <ScanResults
                                scanResults={selectedScan.scanResults}
                            />
                        </div>
                    ) : (
                        <div className="flex h-full items-center justify-center">
                            <AppText
                                variant="regular"
                                className="text-neutral-500"
                            >
                                No scans available
                            </AppText>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};
